import { useTranslation } from 'react-i18next';

interface RankingParticipantCopy {
  clipsTitle: string;
  clipsHelp: string;
  clipsEmpty: string;
  legacyTitle: string;
  legacyHelp: string;
  countedViews: string;
  viewsUnavailable: string;
  trackedViewsUnavailable: string;
  viewsTitle: string;
  totalTrackedViews: string;
  totalTrackedHelp: string;
  rankingViews: string;
  rankingViewsHelp: string;
  pendingViews: string;
  pendingClips: (count: number) => string;
  partialViews: string;
  legacyViewsHelp: string;
  rejectedViewsHelp: string;
  viewsRefreshHelp: string;
  rankingPeriodHelp: string;
  clipTrackedViews: string;
  pendingClipViewsHelp: string;
  rejectedClipViewsHelp: string;
  approvedViewsUnavailable: string;
  approvedClipViewsHelp: string;
  publicViewsUnverified: string;
  viewsCapturedAt: string;

  submittedAt: string;
  individualHelp: string;
  provisionalDisclaimer: string;
  progressTitle: string;
  qualificationTarget: string;
  nextPositionTarget: (position: string) => string;
  viewsNeeded: (views: string) => string;
  clipEstimate: (clips: string) => string;
  estimateHelp: string;
  estimateUnavailable: string;
  leader: string;
  tieReview: string;
}

const en: RankingParticipantCopy = {
  clipsTitle: 'Your submitted clips',
  clipsHelp: 'Each published clip is reviewed individually. Approved clips contribute tracked views to the provisional ranking, with final administrator validation.',
  clipsEmpty: 'No clips submitted yet. Add the link to each published clip using the form above.',
  legacyTitle: 'Previous report corrections',
  legacyHelp: 'These reports belong to the earlier reporting flow. New clips use the individual URL form.',
  countedViews: 'Provisional counted views',
  viewsUnavailable: 'Views are not available yet. They are not counted as zero.',
  trackedViewsUnavailable: 'Current platform views are not available yet. Any counted ranking views are shown separately.',
  viewsTitle: 'Your views',
  legacyViewsHelp: 'Tracked and awaiting-approval totals cover individually submitted clips. Earlier reports are shown separately; their approved views remain in your ranking score.',
  totalTrackedViews: 'Views tracked on your clips',
  totalTrackedHelp: 'Latest available counts across your submitted clips.',
  rankingViews: 'Counted in ranking',
  rankingViewsHelp: 'Your current score from approved, eligible clips.',
  pendingViews: 'Views awaiting approval',
  pendingClips: count => `${count} ${count === 1 ? 'clip awaits' : 'clips await'} approval. These views are not counted in the ranking yet.`,
  partialViews: 'Some clips do not have a view count yet. Tracked totals include only available measurements; — means unavailable, not zero.',
  rejectedViewsHelp: 'The tracked total includes rejected clips. Their views do not enter the ranking.',
  viewsRefreshHelp: 'Connected-account views refresh twice daily when platform access is available, including while clips await approval. Approved clips keep gaining eligible ranking views until the campaign ends.',
  rankingPeriodHelp: 'The ranking uses eligible views from the campaign period. Reviewed or final scores can differ from current platform totals.',
  clipTrackedViews: 'Views tracked on this clip',
  pendingClipViewsHelp: 'Awaiting approval to enter the ranking. Available views are already being tracked while this clip is under review.',
  rejectedClipViewsHelp: 'This clip was rejected. Its tracked views do not count in the ranking.',
  approvedViewsUnavailable: 'This clip is approved, but its eligible ranking count is not available yet.',
  approvedClipViewsHelp: 'Approved. Eligible views keep updating in the ranking through the campaign counting period.',
  publicViewsUnverified: 'Public views · unverified',
  viewsCapturedAt: 'Views checked on',

  submittedAt: 'Submitted',
  individualHelp: 'Submit one published clip URL at a time. Ownership and campaign rules are checked for each clip.',
  provisionalDisclaimer: 'The ranking is subject to administrator review and may change until final validation.',
  progressTitle: 'Keep climbing',
  qualificationTarget: 'Reach the qualifying minimum',
  nextPositionTarget: position => `Aim for position ${position}`,
  viewsNeeded: views => `${views} additional counted views needed.`,
  clipEstimate: clips => `About ${clips} more clips (estimate)`,
  estimateHelp: 'Estimate based on your own provisional counted average views per approved clip. It does not guarantee a position or prize.',
  estimateUnavailable: 'An estimate will be available after your first clips are reviewed.',
  leader: 'You are leading. Keep submitting quality clips to defend your position.',
  tieReview: 'Your tied position is awaiting administrator review.',
};

const pt: RankingParticipantCopy = {
  clipsTitle: 'Seus clipes enviados',
  clipsHelp: 'Cada clipe publicado é avaliado individualmente. Clipes aprovados somam visualizações acompanhadas ao ranking provisório, com validação final dos administradores.',
  clipsEmpty: 'Nenhum clipe enviado ainda. Adicione o link de cada clipe publicado no formulário acima.',
  legacyTitle: 'Correções de relatórios anteriores',
  legacyHelp: 'Estes relatórios pertencem ao fluxo anterior. Novos clipes são enviados individualmente pelo link.',
  countedViews: 'Visualizações contabilizadas provisoriamente',
  viewsUnavailable: 'Visualizações ainda indisponíveis. Elas não são contabilizadas como zero.',
  trackedViewsUnavailable: 'As views atuais na plataforma ainda não estão disponíveis. As views contabilizadas no ranking aparecem separadamente.',
  viewsTitle: 'Suas visualizações',
  legacyViewsHelp: 'Os totais acompanhados e aguardando aprovação incluem clipes enviados individualmente. Relatórios anteriores aparecem separadamente; as views aprovadas deles permanecem na sua pontuação do ranking.',
  totalTrackedViews: 'Views acompanhadas nos seus clipes',
  totalTrackedHelp: 'Últimas contagens disponíveis dos seus clipes enviados.',
  rankingViews: 'Views no ranking',
  rankingViewsHelp: 'Sua pontuação atual com clipes aprovados e elegíveis.',
  pendingViews: 'Views aguardando aprovação',
  pendingClips: count => `${count} ${count === 1 ? 'clipe aguarda' : 'clipes aguardam'} aprovação. Essas views ainda não entram no ranking.`,
  partialViews: 'Alguns clipes ainda não têm contagem de views. Os totais acompanhados incluem apenas as medições disponíveis; — significa indisponível, não zero.',
  rejectedViewsHelp: 'O total acompanhado inclui clipes reprovados. As views deles não entram no ranking.',
  viewsRefreshHelp: 'As views de contas conectadas são atualizadas duas vezes ao dia quando há acesso à plataforma, inclusive durante a análise. Clipes aprovados continuam somando views elegíveis até o fim da campanha.',
  rankingPeriodHelp: 'O ranking usa as views elegíveis do período da campanha. A pontuação revisada ou final pode ser diferente do total atual nas redes.',
  clipTrackedViews: 'Views acompanhadas neste clipe',
  pendingClipViewsHelp: 'Aguardando aprovação para entrar no ranking. As views disponíveis já são acompanhadas enquanto o clipe está em análise.',
  rejectedClipViewsHelp: 'Este clipe foi reprovado. As views acompanhadas dele não contam no ranking.',
  approvedViewsUnavailable: 'Este clipe está aprovado, mas a contagem elegível para o ranking ainda não está disponível.',
  approvedClipViewsHelp: 'Aprovado. As views elegíveis continuam atualizando no ranking durante o período de contagem da campanha.',
  publicViewsUnverified: 'Views públicas · não verificadas',
  viewsCapturedAt: 'Views consultadas em',

  submittedAt: 'Enviado em',
  individualHelp: 'Envie o link de um clipe publicado por vez. A propriedade e as regras da campanha são verificadas para cada clipe.',
  provisionalDisclaimer: 'O ranking está sujeito à avaliação dos administradores e pode mudar até a validação final.',
  progressTitle: 'Continue subindo',
  qualificationTarget: 'Alcance o mínimo para se classificar',
  nextPositionTarget: position => `Busque a posição ${position}`,
  viewsNeeded: views => `Faltam ${views} visualizações contabilizadas.`,
  clipEstimate: clips => `Cerca de ${clips} clipes a mais (estimativa)`,
  estimateHelp: 'Estimativa baseada na sua média provisória de visualizações contabilizadas por clipe aprovado. Ela não garante posição ou prêmio.',
  estimateUnavailable: 'A estimativa ficará disponível depois que seus primeiros clipes forem avaliados.',
  leader: 'Você está na liderança. Continue enviando bons clipes para defender sua posição.',
  tieReview: 'Sua posição empatada aguarda a avaliação dos administradores.',
};

export function useRankingParticipantCopy(): RankingParticipantCopy {
  const { i18n } = useTranslation();
  return (i18n?.resolvedLanguage || i18n?.language || 'en').startsWith('pt') ? pt : en;
}
